/**
 * Formatierer + Status-Vokabular (deutsch) fuer alle Screens.
 * Geld IMMER aus Integer-Cents (Konvention aus packages/db).
 */
import type { EventStatus } from "./types";

// L11 (Antoine, 11.07.2026): Beträge IMMER mit zwei Nachkommastellen ("900,00 €"),
// auch bei glatten Euro-Werten — einheitliche Geld-Darstellung auf allen Screens.
const EUR_CENTS = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" });

export function money(cents: number | null | undefined): string {
  if (cents == null) return "–";
  return EUR_CENTS.format(cents / 100);
}

/**
 * H7/N-F2: Parst einen als de-DE eingegebenen Dezimalwert (Geld/Prozent) zu einem Number.
 * Bewusst konservativ — lieber `null` (Validierungsfehler beim Aufrufer) als ein 1000-fach
 * falscher Betrag durch `parseFloat("1.500") === 1.5`:
 *  - Leerzeichen werden entfernt.
 *  - Komma = Dezimaltrennzeichen; Punkte gelten dann als Tausenderpunkte und fallen weg.
 *  - Ohne Komma werden Punkte als Tausendergruppierung entfernt, wenn jede Nachgruppe genau
 *    3 Ziffern hat („1.500" -> 1500, „1.234.567" -> 1234567); „1.5"/„1.50" bleibt Dezimalpunkt.
 *  - Alles Ungültige, Mehrdeutige (z. B. „1.2.3") oder Negative -> `null`.
 * @returns Number (>= 0) oder `null` bei leer/ungültig/mehrdeutig.
 */
export function parseGermanDecimal(input: string): number | null {
  const s = input.replace(/\s/g, "");
  if (s === "") return null;
  let normalized: string;
  if (s.includes(",")) {
    if ((s.match(/,/g) ?? []).length > 1) return null; // mehrere Kommas -> ungültig
    normalized = s.replace(/\./g, "").replace(",", ".");
  } else if (s.includes(".")) {
    const parts = s.split(".");
    const grouped = parts.length >= 2 && /^\d{1,3}$/.test(parts[0]!) && parts.slice(1).every((g) => /^\d{3}$/.test(g));
    normalized = grouped ? parts.join("") : s;
  } else {
    normalized = s;
  }
  if (!/^\d+(\.\d+)?$/.test(normalized)) return null;
  const n = Number(normalized);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/** Wie {@link parseGermanDecimal}, aber direkt in verlustfrei gerundete Integer-Cents. */
export function parseGermanEurosToCents(input: string): number | null {
  const euros = parseGermanDecimal(input);
  return euros == null ? null : Math.round(euros * 100);
}

/** `locale` optional (Default de-DE) — genutzt von der oeffentlichen Angebotsseite fuer EN-Events. */
export function formatDate(value: string | null | undefined, locale: string = "de-DE"): string {
  if (!value) return "–";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "–";
  return d.toLocaleDateString(locale, { day: "2-digit", month: "2-digit", year: "numeric" });
}

/** "Samstag, 12. Juli 2026" -- Wochentag + langes Datum fuer Detail-Kopfzeilen. */
export function formatWeekdayDate(value: string | null | undefined): string {
  if (!value) return "–";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "–";
  return d.toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

/** "in 5 Tagen" / "heute" / "vor 2 Tagen" -- relative Tages-Angabe zu einem Datum (ohne Uhrzeit). */
export function daysUntil(value: string | null | undefined): string | null {
  if (!value) return null;
  const target = new Date(value);
  if (Number.isNaN(target.getTime())) return null;
  const today = new Date();
  const t0 = new Date(target.getFullYear(), target.getMonth(), target.getDate()).getTime();
  const d0 = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  const days = Math.round((t0 - d0) / 86_400_000);
  if (days === 0) return "heute";
  if (days === 1) return "morgen";
  if (days === -1) return "gestern";
  if (days > 1) return `in ${days} Tagen`;
  return `vor ${Math.abs(days)} Tagen`;
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return "–";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "–";
  return d.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Relative Angabe ("Vor 2 Std.") fuer Listen; faellt auf Datum zurueck. */
export function timeAgo(value: string | null | undefined): string {
  if (!value) return "–";
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return "–";
  const mins = Math.floor((Date.now() - then) / 60_000);
  if (mins < 1) return "Gerade eben";
  if (mins < 60) return `Vor ${mins} Min.`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `Vor ${hours} Std.`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "Gestern";
  if (days < 7) return `Vor ${days} Tagen`;
  return formatDate(value);
}

/* ----------------------------- Status-Vokabular ---------------------------- */

type PillTone = "neutral" | "info" | "success" | "warn" | "danger" | "primary";

export interface StatusMeta {
  label: string;
  tone: PillTone;
}

export const INQUIRY_STATUS: Record<string, StatusMeta> = {
  new: { label: "Neu", tone: "info" },
  quoted: { label: "Angebot gesendet", tone: "warn" },
  won: { label: "Gewonnen", tone: "success" },
  lost: { label: "Verloren", tone: "neutral" },
};

/** Kanal, über den eine Anfrage hereinkam — mit Material-Icon für die UI-Anzeige. */
export const INQUIRY_SOURCE: Record<string, { label: string; icon: string }> = {
  manual: { label: "Manuell angelegt", icon: "person" },
  form: { label: "Website-Formular", icon: "public" },
  chat: { label: "KI-Chat", icon: "chat" },
  email: { label: "E-Mail", icon: "mail" },
  phone: { label: "Telefon", icon: "call" },
};
export function inquirySourceMeta(source: string | null | undefined): { label: string; icon: string } {
  return (source && INQUIRY_SOURCE[source]) || { label: source || "Unbekannt", icon: "help" };
}

export const EVENT_STATUS: Record<EventStatus, StatusMeta> = {
  inquiry: { label: "Anfrage", tone: "info" },
  offer_draft: { label: "Entwurf", tone: "neutral" },
  offer_sent: { label: "Angebot gesendet", tone: "warn" },
  offer_chosen: { label: "Angebot gewählt", tone: "success" },
  offer_declined: { label: "Abgelehnt", tone: "danger" },
  paid: { label: "Bezahlt", tone: "success" },
  completed: { label: "Durchgeführt", tone: "info" },
  cancelled: { label: "Storniert", tone: "danger" },
  payment_failed: { label: "Zahlung fehlgeschlagen", tone: "danger" },
  no_response: { label: "Keine Antwort", tone: "neutral" },
};

/** N-11: sicherer Lookup — unbekannte/neue Event-Status crashen die UI nicht mehr (roher Code als
 *  Label, neutraler Ton als Fallback statt `EVENT_STATUS[status]` === undefined). */
export function eventStatusMeta(status: string): StatusMeta {
  return EVENT_STATUS[status as EventStatus] ?? { label: status, tone: "neutral" };
}

export const PAYMENT_STATUS: Record<string, StatusMeta> = {
  pending: { label: "Offen", tone: "warn" },
  processing: { label: "In Bearbeitung", tone: "info" },
  succeeded: { label: "Bezahlt", tone: "success" },
  failed: { label: "Fehlgeschlagen", tone: "danger" },
  refunded: { label: "Erstattet", tone: "neutral" },
  canceled: { label: "Storniert", tone: "neutral" },
};

export const PAYMENT_TYPE_LABEL: Record<string, string> = {
  deposit: "Anzahlung",
  prepayment: "Vorauszahlung",
  final: "Restzahlung",
  full: "Gesamtbetrag",
};

export const ROLE_LABEL: Record<string, string> = {
  owner: "Inhaber",
  admin: "Admin",
  staff: "Mitarbeiter",
};

// Claude-Design-Entwurf: warme, gedeckte Ton-Paare statt generischer Tailwind-100/800-Farben.
// Dark-Mode: translucente warme Töne (nie helle Vollflächen — sonst leuchten die Pills im Nacht-Layout).
export const PILL_CLASSES: Record<PillTone, string> = {
  neutral: "bg-[#F0EAE0] text-[#6F675A] dark:bg-white/[0.08] dark:text-[#b8ae9e]",
  info: "bg-[#E7EEF7] text-[#31619B] dark:bg-[#31619B]/20 dark:text-[#9dbbe0]",
  success: "bg-[#E4F1E8] text-[#217A50] dark:bg-[#2E9963]/18 dark:text-[#7bc49a]",
  warn: "bg-[#FAEFD9] text-[#96650D] dark:bg-[#C98A1B]/20 dark:text-[#e0b366]",
  danger: "bg-[#F8E7E3] text-[#B23B32] dark:bg-[#B23B32]/22 dark:text-[#e39b92]",
  primary: "bg-primary/10 text-primary dark:bg-primary/20 dark:text-[#f0b49b]",
};
